import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddressValidationResultModel } from '@/Models/AddressValidationResultModel';
import { AddressValidationResultType, LoadingType } from '@/Enums';
import { ShippingLabelStep } from '@/ModalWindows/ShippingLabelModal/Types';
import { asyncActions } from './asyncActions';
import { OrderWarehouseInfo } from '@/Models/Order/OrderWarehouseInfo';
import { shippingLabelModalUtils } from '@/ModalWindows/ShippingLabelModal/services/utils';
import { orderMapping } from '@/Mapping/OrderMapping';

export type ShippingLabelModalState = {
  validationConflicts: AddressValidationResultModel[];
  isOnlyAddressValidation: boolean;
  currentConflictIndex: number;
  loadingType: LoadingType;
  currentStep: ShippingLabelStep;
  isEditing: boolean;
  orderWarehouseInfos: OrderWarehouseInfo[];
  filteredOrderIdsByWarehouse: number[];
};

const initialState: ShippingLabelModalState = {
  validationConflicts: [],
  isOnlyAddressValidation: false,
  currentConflictIndex: 0,
  loadingType: LoadingType.None,
  currentStep: ShippingLabelStep.None,
  isEditing: false,
  orderWarehouseInfos: [],
  filteredOrderIdsByWarehouse: [],
};

const shippingLabelSlice = createSlice({
  name: 'shippingLabelModal',
  initialState,
  reducers: {
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setShippingLabelStep: (state, action: PayloadAction<ShippingLabelStep>) => {
      state.currentStep = action.payload;
    },
    setOnlyValidation: (state) => {
      state.isOnlyAddressValidation = true;
    },
    setFilteredOrderIds: (state, action: PayloadAction<number[]>) => {
      state.filteredOrderIdsByWarehouse = action.payload;
      state.currentStep = ShippingLabelStep.OrderWithoutAutomatizaion;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.validateOrderAddresses.fulfilled, (state, action) => {
        state.currentConflictIndex = 0;
        state.validationConflicts = action.payload.compares.filter(
          (c) =>
            c.result === AddressValidationResultType.NotFound ||
            c.result === AddressValidationResultType.FoundWithChanges,
        );
        state.loadingType = LoadingType.Loaded;

        if (state.validationConflicts.length > 0) {
          state.currentStep = ShippingLabelStep.Validation;
        } else if (state.isOnlyAddressValidation) {
          state.currentStep = ShippingLabelStep.Close;
        } else {
          state.currentStep = ShippingLabelStep.SelectWarehouse;
        }
      })
      .addCase(asyncActions.applyAddressChanges.fulfilled, (state, action) => {
        state.validationConflicts[state.currentConflictIndex].suggestedAddress = orderMapping.getShippingInfo(
          action.payload.order,
        );
        state.currentConflictIndex = state.currentConflictIndex + 1;

        if (state.currentConflictIndex > state.validationConflicts.length - 1) {
          state.currentStep = state.isOnlyAddressValidation
            ? ShippingLabelStep.Close
            : ShippingLabelStep.SelectWarehouse;
        }
      })
      .addCase(asyncActions.getOrdersWarehouseInfo.fulfilled, (state, action) => {
        const { orders } = action.payload;
        const warehouseOptions = shippingLabelModalUtils.getWarehouseOptions(orders);

        if (warehouseOptions.length <= 1) {
          state.filteredOrderIdsByWarehouse = action.meta.arg.orderIds;
          state.currentStep = ShippingLabelStep.OrderWithoutAutomatizaion;
        } else {
          state.orderWarehouseInfos = orders;
        }
      });
  },
});

const { actions, reducer } = shippingLabelSlice;

export const shippingLabelModalReducer = reducer;
export const shippingLabelModalActions = actions;
export const shippingLabelModalAsyncActions = asyncActions;
